export const PDF_MIME_TYPE = 'application/pdf';

export function base64ToBytes(str: string): Uint8Array {
  return Uint8Array.from(atob(str), (char) => char.charCodeAt(0));
}

/**
 * Hint: Recommended way for file handling: let the browser decide...
 * Tries to open a new Tab in the Browser and displays the file. Browser itself will decide wether it will be displayed or if it will be downloaded.
 * Hint: Client side files will be opened in a new tab, server side files are downloaded most frequently from the browser.
 * So, it is possible to directly insert an url to static file server side/storage.
 * @param url
 */
export const openFileInNewTab = (url: string) =>
  window.open(url, '_blank', 'noopener,noreferrer');

/**
 *   Hint: Use this for client side files that should be rendered in a new tab.
 *   Opens a new Tab and shows the PDF directly in the browser
 *   (if Browser has PDF viewer)
 *   Disadvantage: we cannot set the file name here
 *   but in use-cases where the normaly wants to print the pdf
 *   that doesn't matter much
 *    (e.g. Bescheinigung that needs to be printed, singed and sent as letter)
 * @param content
 */
export function openBlobInNewTab(content: Blob | File) {
  const url = window.URL.createObjectURL(content);

  openFileInNewTab(url);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export function fileToString(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target && event.target.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error('FileReader onload event triggered without result'));
      }
    };

    reader.onerror = (error) => {
      reject(new Error(`FileReader encountered an error: ${error}`));
    };

    reader.readAsText(file);
  });
}

/**
 * Converts the contents of a given `File` object into a text string.
 *
 * @param {File} [file] - The file to convert. Can be `undefined`.
 * @returns {Promise<string | undefined>} A promise that resolves to the text content of the file,
 * or `undefined` if the file is not provided or empty.
 */
export const convertBlobToTextString = async (
  file?: File
): Promise<string | undefined> => {
  if (!file) {
    console.warn('No file provided');
    return undefined;
  }

  if (file.size === 0) {
    console.warn('File is empty');
    return undefined;
  }

  return await file.text();
};

export const fileToDataURL = (file: File | Blob): Promise<string> =>
  new Promise(
    (
      resolve: (value: string | PromiseLike<string>) => void,
      reject: (reason?: any) => void
    ) => {
      const reader: FileReader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.readAsDataURL(file);
      reader.onerror = (error: ProgressEvent<FileReader>) => reject(error);
    }
  );

export const fileToLogoV0 = (file: File) => {
  return fileToDataURL(file).then((base64Result) => {
    return {
      fileName: file.name,
      fileType: base64Result.split(':')[1].split(';')[0],
      base64Data: base64Result.split(',')[1],
      size: file.size
    };
  });
};
